import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCurrentUser, isAuthenticated, signOut } from './lib/auth';
import { useIdleTimeout } from './lib/idle-timeout';
import { useIsMobile } from './lib/use-viewport';
import type { ListSubmissionsRequest } from './types/moderation';
import LoginScreen from './components/LoginScreen';
import Sidebar from './components/Sidebar';
import MobileShell from './components/MobileShell';
import MobileQueue from './components/MobileQueue';
import Dashboard from './components/Dashboard';
import SubmissionList from './components/SubmissionList';
import SubmissionDetail from './components/SubmissionDetail';
import ActivityLog from './components/ActivityLog';
import GuidePage from './components/GuidePage';
import IssuesPage from './components/IssuesPage';
import SecuritySettings from './components/SecuritySettings';
import KeyboardHelp from './components/KeyboardHelp';
import CommandPalette from './components/CommandPalette';
import ToastContainer from './components/ui/Toast';
import { ReportProblemDialog, ReportProblemTrigger } from './components/ReportProblem';
import Tooltip from './components/ui/Tooltip';
import { localTimezoneLabel } from './lib/format';
import { ANNOUNCE_EVENT } from './lib/announce';
import { getSubmissionNavCache } from './lib/submission-nav';
import type { PaletteCommand } from './components/CommandPalette';
import { moderatorGuideMarkdown } from './lib/moderator-guide';

/**
 * G-6: build palette commands from the guide's `##` headings so the
 * moderator can jump straight to a section via Cmd+K → section name.
 * Reads the markdown source once (it's bundled inline) so the section
 * list never drifts from the rendered page.
 */
function guideSectionCommands(): PaletteCommand[] {
  const slugify = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  const out: PaletteCommand[] = [];
  for (const line of moderatorGuideMarkdown.split('\n')) {
    const match = /^##\s+(.+?)\s*$/.exec(line);
    if (match) {
      const text = match[1].trim();
      const slug = slugify(text);
      out.push({
        id: `guide-section-${slug}`,
        label: text,
        hint: 'Moderator guide',
        hash: `#/guide#${slug}`,
        group: 'Guide sections',
      });
    }
  }
  return out;
}

const GUIDE_SECTION_COMMANDS = guideSectionCommands();

interface ParsedRoute {
  path: string;
  params: URLSearchParams;
}

function parseHash(): ParsedRoute {
  const raw = window.location.hash || '#/';
  const [pathPart, queryPart] = raw.split('?');
  return {
    path: pathPart || '#/',
    params: new URLSearchParams(queryPart || ''),
  };
}

function filtersFromParams(params: URLSearchParams): ListSubmissionsRequest {
  const out: ListSubmissionsRequest = {};
  if (params.has('status')) out.status = params.get('status')!;
  if (params.has('theme')) out.theme = params.get('theme')!;
  if (params.has('decade')) out.decade = params.get('decade')!;
  if (params.has('mediaType')) out.mediaType = params.get('mediaType')!;
  if (params.has('search')) out.search = params.get('search')!;
  if (params.has('contributorEmail')) {
    out.contributorEmail = params.get('contributorEmail')!;
  }
  if (params.has('sort')) {
    out.sort = params.get('sort') as ListSubmissionsRequest['sort'];
  }
  if (params.has('dir')) {
    const dir = params.get('dir');
    if (dir === 'asc' || dir === 'desc') out.dir = dir;
  }
  if (params.has('sensitive')) {
    out.sensitive = params.get('sensitive') === 'true';
  }
  return out;
}

// `announce()` is exported from `./lib/announce` so downstream test files
// can import it without dragging in the rest of the app shell.

export default function App() {
  const [authed, setAuthed] = useState<boolean>(() => isAuthenticated());
  const [route, setRoute] = useState<ParsedRoute>(() => parseHash());
  const [currentUser, setCurrentUser] = useState(() => getCurrentUser());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // One "Report a problem" dialog, opened from the sidebar or More sheet.
  const [reportOpen, setReportOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [signOutBanner, setSignOutBanner] = useState<string | null>(null);
  const [liveMessage, setLiveMessage] = useState('');

  useEffect(() => {
    const handler = () => setRoute(parseHash());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  // Wire the announce() helper to a single aria-live region at the app root.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ message: string }>).detail;
      if (!detail?.message) return;
      // Reset to empty first to ensure repeat messages re-announce.
      setLiveMessage('');
      setTimeout(() => setLiveMessage(detail.message), 50);
    };
    window.addEventListener(ANNOUNCE_EVENT, handler);
    return () => window.removeEventListener(ANNOUNCE_EVENT, handler);
  }, []);

  const navigate = useCallback((hash: string) => {
    if (window.location.hash === hash) {
      setRoute(parseHash());
      return;
    }
    window.location.hash = hash;
    setSidebarOpen(false);
  }, []);

  const handleAuthenticated = () => {
    setAuthed(true);
    setSignOutBanner(null);
    setCurrentUser(getCurrentUser());
  };

  const handleSignOut = useCallback(() => {
    signOut();
    setAuthed(false);
    setCurrentUser(null);
  }, []);

  const handleIdle = useCallback(
    (reason: 'idle' | 'absolute') => {
      handleSignOut();
      setSignOutBanner(
        reason === 'idle'
          ? 'You were signed out due to inactivity.'
          : 'Your session expired. Please sign in again.',
      );
    },
    [handleSignOut],
  );

  useIdleTimeout(authed, handleIdle);

  // Global keyboard handlers: ? help, ⌘K palette, Esc closes. Other
  // shortcuts (j/k/a/r/w/c) are scoped to the SubmissionDetail screen.
  useEffect(() => {
    const isEditableTarget = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const handler = (e: KeyboardEvent) => {
      // ⌘K / Ctrl+K — toggle the command palette. Allowed even from inputs
      // so the moderator can jump out of a search box quickly.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape') {
        if (paletteOpen) setPaletteOpen(false);
        else if (helpOpen) setHelpOpen(false);
        return;
      }
      if (isEditableTarget(e.target)) return;
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [paletteOpen, helpOpen]);

  const isMobile = useIsMobile();

  const content = useMemo(() => {
    const path = route.path;
    if (path.startsWith('#/submissions/')) {
      const id = decodeURIComponent(path.replace('#/submissions/', ''));
      return <SubmissionDetail submissionId={id} onBack={() => navigate('#/submissions')} />;
    }
    if (path.startsWith('#/submissions')) {
      // Mobile uses a thumbnail-led feed; desktop keeps the data table.
      // Both call into the same `listSubmissions` API and hand the
      // selected submission ID back to the router.
      if (isMobile) {
        return (
          <MobileQueue
            initialFilters={filtersFromParams(route.params)}
            onSelect={(id) => navigate(`#/submissions/${encodeURIComponent(id)}`)}
          />
        );
      }
      return (
        <SubmissionList
          initialFilters={filtersFromParams(route.params)}
          onSelect={(id) => navigate(`#/submissions/${encodeURIComponent(id)}`)}
        />
      );
    }
    if (path.startsWith('#/activity')) {
      return (
        <ActivityLog
          onSelect={(id) => navigate(`#/submissions/${encodeURIComponent(id)}`)}
        />
      );
    }
    if (path.startsWith('#/issues/')) {
      const id = decodeURIComponent(path.replace('#/issues/', ''));
      return <IssuesPage issueId={id} onNavigate={navigate} />;
    }
    if (path.startsWith('#/issues')) return <IssuesPage onNavigate={navigate} />;
    if (path.startsWith('#/guide')) return <GuidePage />;
    if (path.startsWith('#/settings/security')) {
      return <SecuritySettings onBack={() => navigate('#/')} />;
    }
    return <Dashboard onNavigate={navigate} />;
  }, [route, navigate, isMobile]);

  const tz = localTimezoneLabel();

  if (!authed) {
    return (
      <LoginScreen
        onAuthenticated={handleAuthenticated}
        banner={signOutBanner || undefined}
      />
    );
  }

  // Mobile shell: pride flag gradient bar, compact topbar, bottom tab
  // bar, and an in-app "More" sheet for sign-out + utility links. The
  // command palette, keyboard help, and toast container all remain
  // mounted at the root so deep-link / keyboard flows still work.
  if (isMobile) {
    return (
      <>
        <a href="#main-content" className="skip-link" data-testid="skip-link">
          Skip to main content
        </a>
        <MobileShell
          currentPath={route.path}
          onNavigate={navigate}
          onSignOut={handleSignOut}
          onOpenKeyboardHelp={() => setHelpOpen(true)}
          onReportProblem={() => setReportOpen(true)}
          userEmail={currentUser?.email}
          userRole={currentUser?.role}
        >
          {content}
        </MobileShell>
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          data-testid="live-region"
        >
          {liveMessage}
        </div>
        <KeyboardHelp open={helpOpen} onOpenChange={setHelpOpen} />
        <CommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          onNavigate={navigate}
          commands={(() => {
            if (!paletteOpen) return [];
            const recent = getSubmissionNavCache().recent;
            return recent.map<PaletteCommand>((r) => ({
              id: `recent-${r.id}`,
              label: r.title,
              hint: r.id,
              hash: `#/submissions/${encodeURIComponent(r.id)}`,
              group: 'Recent submissions',
            }));
          })()}
        />
        <ToastContainer />
        <ReportProblemDialog open={reportOpen} onClose={() => setReportOpen(false)} />
      </>
    );
  }

  return (
    <div className="app-shell">
      {/* F-007: skip link for keyboard users. Visually hidden until
          focused; then absolutely positioned in the top-left. */}
      <a href="#main-content" className="skip-link" data-testid="skip-link">
        Skip to main content
      </a>
      <header className="app-topbar">
        <button
          type="button"
          className="app-topbar__menu"
          aria-label="Toggle navigation"
          aria-expanded={sidebarOpen}
          aria-controls="primary-nav"
          onClick={() => setSidebarOpen((open) => !open)}
          data-testid="topbar-menu"
        >
          <span aria-hidden="true">☰</span>
        </button>
        <div className="app-topbar__brand">Brisbane Pride Moderation</div>
        <div className="app-topbar__user">
          {tz && (
            <Tooltip content={`Times displayed in your local timezone (${tz})`}>
              <span
                className="app-topbar__tz"
                data-testid="local-tz-label"
                tabIndex={0}
              >
                {tz} (local time)
              </span>
            </Tooltip>
          )}
          <ReportProblemTrigger
            onClick={() => setReportOpen(true)}
            shortLabel="Report"
            data-testid="topbar-report-problem"
          />
          {/* F-111: Tooltip primitive replaces the native title=
              attribute on these icon buttons (title= never appears
              on focus and is invisible on touch). */}
          <Tooltip content="Command palette (Cmd-K)">
            <button
              type="button"
              className="btn btn-secondary btn-icon"
              onClick={() => setPaletteOpen(true)}
              aria-label="Open command palette"
              aria-keyshortcuts="Meta+k Control+k"
              data-testid="open-palette"
            >
              <span aria-hidden="true">⌘K</span>
            </button>
          </Tooltip>
          <Tooltip content="Keyboard shortcuts (?)">
            <button
              type="button"
              className="btn btn-secondary btn-icon"
              onClick={() => setHelpOpen(true)}
              aria-label="Show keyboard shortcuts"
              aria-keyshortcuts="?"
              data-testid="open-help"
            >
              <span aria-hidden="true">?</span>
            </button>
          </Tooltip>
          {currentUser && (
            <>
              <span data-testid="current-user">{currentUser.email}</span>
              <span
                className={`role-chip role-chip--${currentUser.role}`}
                data-testid="current-user-role"
              >
                {currentUser.role.replace('_', ' ')}
              </span>
            </>
          )}
          <button type="button" className="btn btn-secondary" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </header>
      <div className="app-body">
        <div
          id="primary-nav"
          className={`app-sidebar-wrapper${sidebarOpen ? ' is-open' : ''}`}
        >
          <Sidebar
            currentPath={route.path}
            onNavigate={navigate}
            userRole={currentUser?.role}
          />
        </div>
        {sidebarOpen && (
          <button
            type="button"
            className="app-sidebar-scrim"
            aria-label="Close navigation"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <main className="app-main" id="main-content" tabIndex={-1}>
          {content}
        </main>
      </div>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="live-region"
      >
        {liveMessage}
      </div>
      <KeyboardHelp open={helpOpen} onOpenChange={setHelpOpen} />
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onNavigate={navigate}
        commands={(() => {
          // F-211: feed the palette with cached recent submissions so a
          // moderator typing a partial title finds something. Refreshed
          // each time the palette opens.
          if (!paletteOpen) return [];
          const recent = getSubmissionNavCache().recent;
          const recentCommands = recent.map<PaletteCommand>((r) => ({
            id: `recent-${r.id}`,
            label: r.title,
            hint: r.id,
            hash: `#/submissions/${encodeURIComponent(r.id)}`,
            group: 'Recent submissions',
          }));
          // G-6: surface every guide section so `Cmd+K → "Sensitivity"
          // → Enter` lands on the matching anchor in #/guide.
          return [...recentCommands, ...GUIDE_SECTION_COMMANDS];
        })()}
      />
      <ToastContainer />
      <ReportProblemDialog open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}
